import React from 'react';
import { Avatar, Box, Checkbox, Typography } from '@mui/material';
import './EftTable.css';
import PayoutTableSkeleton from '../../../SkeletonEffect/PayoutTableSkeleton/PayoutTableSkeleton';

// Same green as Success in PayoutTable, so the panel reads as one system.
const ELIGIBLE_STATUS = { color: '#14B334', bgcolor: '#F2FAF3' };

const SOURCE_LABEL = {
  student_session_payment: 'Session Payment',
  instructor_enrollment_fee: 'Enrollment Fee',
};

export function EligiblePaymentsTable({
  data,
  isLoading = false,
  selectedIds = [],
  allSelected = false,
  onToggleOne,
  onToggleAll,
}) {
  if (isLoading) {
    return <PayoutTableSkeleton />;
  }

  const rows = data || [];
  // `allSelected` is the whole filtered set, across every page. Otherwise fall
  // back to this page: indeterminate when partly ticked, so the header box
  // never claims a selection the admin did not make.
  const selectedOnPage = rows.filter(row => selectedIds.includes(row.id));
  const allOnPageSelected =
    allSelected || (rows.length > 0 && selectedOnPage.length === rows.length);
  // selectedIds spans pages, so a selection made elsewhere still shows here.
  const anySelected = allSelected || selectedIds.length > 0;
  const someOnPageSelected = anySelected && !allOnPageSelected;

  return (
    <Box className="eft-table-container">
      <table className="eft-table">
        <thead>
          <tr>
            <th className="eft-th eft-checkbox-cell">
              <Checkbox
                size="small"
                checked={allOnPageSelected}
                indeterminate={someOnPageSelected}
                // Anything selected -> clear. Only an empty selection selects
                // all, so the half-ticked box undoes rather than sweeps.
                onChange={() => onToggleAll(!anySelected)}
                disabled={rows.length === 0}
                inputProps={{ 'aria-label': 'Select all eligible payments' }}
              />
            </th>
            <th className="eft-th">Payment ID</th>
            <th className="eft-th">Learner / Instructor</th>
            <th className="eft-th">Payment For</th>
            <th className="eft-th">Payment Date</th>
            <th className="eft-th">Eligible Date</th>
            <th className="eft-th">Amount (QAR)</th>
            <th className="eft-th">Status</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td colSpan={8} className="eft-empty-cell">
                <Box className="eft-empty-state">
                  <Typography className="eft-empty-text">
                    No eligible payments for the selected filters
                  </Typography>
                </Box>
              </td>
            </tr>
          ) : (
            rows.map(row => (
              <tr key={row.id} className="eft-row">
                <td className="eft-td eft-checkbox-cell">
                  <Checkbox
                    size="small"
                    checked={allSelected || selectedIds.includes(row.id)}
                    onChange={() => onToggleOne(row)}
                    inputProps={{ 'aria-label': `Select payment ${row.id}` }}
                  />
                </td>
                <td className="eft-td">{row.id}</td>
                <td className="eft-td">
                  <Box className="eft-name-container">
                    <Avatar
                      sx={{ width: 25, height: 25 }}
                      src={row.payerImage || undefined}
                      alt={row.payerName}
                    />
                    <Typography className="eft-name-text">{row.payerName}</Typography>
                  </Box>
                </td>
                <td className="eft-td">{row.paymentFor}</td>
                <td className="eft-td">{row.paidAt}</td>
                <td className="eft-td">{row.eligibleAt}</td>
                <td className="eft-td">{row.amount}</td>
                <td className="eft-td">
                  <Box className="eft-status-container">
                    <Box
                      className="eft-status-badge"
                      sx={{
                        border: `1px solid ${ELIGIBLE_STATUS.color}`,
                        backgroundColor: ELIGIBLE_STATUS.bgcolor,
                        color: ELIGIBLE_STATUS.color,
                      }}
                    >
                      <Box
                        className="eft-status-dot"
                        sx={{ backgroundColor: ELIGIBLE_STATUS.color }}
                      />
                      <Typography className="eft-status-text">Eligible</Typography>
                    </Box>
                  </Box>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </Box>
  );
}

export { SOURCE_LABEL };
